import { PartState } from '../types';

/**
 * Facing right state — Bizy rotated significantly to the right.
 * Only the right eye is visible; the left eye collapses to the body edge.
 * Badge is hidden (on the far side). Wings are bunched on the left (rear).
 * Left arm wraps in front, right arm behind body.
 */
export const facingRightState: PartState = {
	antenna1: {
		path: 'M148.714 36.02C145.954 36.02 143.714 38.26 143.714 41.02C143.714 43.78 145.954 46.02 148.714 46.02C150.344 46.02 151.794 45.24 152.704 44.03C158.854 45.30 163.764 48.79 167.764 53.03C172.484 58.30 175.354 64.60 176.514 68.71C176.894 70.04 178.274 70.81 179.604 70.43C180.934 70.05 181.704 68.67 181.324 67.34C179.984 62.61 176.794 55.60 171.484 49.67C167.084 44.75 161.124 40.48 153.504 39.02C152.734 37.24 150.964 36.02 148.714 36.02Z',
	},
	antenna2: {
		path: 'M134.694 36.02C131.924 36.02 129.684 38.26 129.684 41.02C129.684 43.78 131.924 46.02 134.694 46.02C136.434 46.02 137.964 45.15 138.864 43.81C145.634 44.83 150.984 48.50 155.054 53.03C159.784 58.30 162.654 64.60 163.814 68.71C164.194 70.04 165.574 70.81 166.904 70.43C168.234 70.05 169.004 68.67 168.624 67.34C167.284 62.61 164.094 55.60 158.784 49.67C154.064 44.27 147.564 39.47 138.164 38.34C137.344 36.70 135.644 36.02 134.694 36.02Z',
	},
	body: { x: 116, y: 62, width: 100, height: 162, rx: 32 },
	bodyHighlight: { x: 116, y: 62, width: 87, height: 162, rx: 32 },
	stripes: { y1: 140, y2: 169, y3: 198, height: 15 },
	eyes: {
		// Left eye slides past body right edge (x=216) — clipped away by body clip path
		leftPath:
			'M225 100.5C225 97.462 227.462 95 230.5 95C233.538 95 236 97.462 236 100.5C236 100.5 236 107.5 236 107.5C236 110.538 233.538 113 230 113C229.5 113 229.5 113 229 113C227.462 113 225 110.538 225 107.5C225 107.5 225 100.5 225 100.5Z',
		// Right eye visible within body bounds
		rightPath:
			'M192 100.5C192 97.462 194.462 95 197.5 95C200.538 95 203 97.462 203 100.5C203 100.5 203 107.5 203 107.5C203 110.538 200.538 113 198 113C197.5 113 197.5 113 197 113C194.462 113 192 110.538 192 107.5C192 107.5 192 100.5 192 100.5Z',
	},
	wing1Path:
		'M82.262 133.33C94.051 155.59 123.142 152.03 134.326 150.5C135.192 150.33 135.654 148.54 135.156 147.3C121.049 112.11 93.767 86.14 79.595 75.21C64.931 63.99 71.919 115.71 82.262 133.33Z',
	wing2Path:
		'M79.915 157.891C92.498 174.551 121.405 150.981 131.238 142.191C132.021 141.531 132.098 139.591 131.38 138.701C113.818 112.071 81.871 104.931 67.995 102.201C53.669 99.451 68.001 149.241 79.915 157.891Z',
	wing3Path:
		'M86.623 133.33C98.412 155.59 127.502 152.03 138.687 150.5C139.552 150.33 140.015 148.54 139.517 147.3C125.41 112.11 98.127 86.14 83.955 75.21C69.291 63.99 76.28 115.71 86.623 133.33Z',
	wing4Path:
		'M84.275 157.891C96.859 174.551 125.766 150.981 135.599 142.191C136.381 141.531 136.458 139.591 135.741 138.701C118.179 112.071 86.231 104.931 72.356 102.201C58.03 99.451 72.362 149.241 84.275 157.891Z',
	arms: [
		{ d: 'M167 139L171 166.7C171.4 168 171.4 168.5 171.8 169.12L185 183', side: 'left' },
		{ d: 'M185 140.17L191 167.8C191.17 168.68 191.57 169.5 192.14 170.2L201 180.87', side: 'right' },
	],
	armOrder: 'leftFront',
	antennaOrder: 'leftFront',
	stinger:
		'M124.478 231.41C127.948 221.45 139.298 228.41 134.468 236.06C130.318 242.62 122.028 248.33 115.648 248.75C114.408 248.83 114.038 247.39 114.988 246.59C120.528 241.92 121.308 240.28 124.478 231.41Z',
	badge: {
		// Badge slides past body left edge (x=116) — clipped away by body clip path
		outerEllipse: { cx: 100, cy: 143.76, rx: 14.1, ry: 10.86 },
		innerEllipse: { cx: 100, cy: 143.76, rx: 12.19, ry: 9.34 },
		starPath:
			'M100 130.58C100.75 130.58 101.4 131.09 101.6 131.82L103.55 139.24L109.09 142.47C109.6 142.77 109.91 143.32 109.91 143.91C109.91 144.51 109.6 145.06 109.09 145.36L103.55 148.59L101.6 156.01C101.4 156.74 100.75 157.25 100 157.25C99.25 157.25 98.6 156.74 98.4 156.01L96.45 148.59L90.91 145.36C90.4 145.06 90.09 144.51 90.09 143.91C90.09 143.32 90.4 142.77 90.91 142.47L96.45 139.24L98.4 131.82C98.6 131.09 99.25 130.58 100 130.58Z',
		starHighlightPath:
			'M101.6 131.82C101.4 131.09 100.75 130.58 100 130.58C99.25 130.58 98.6 131.09 98.4 131.82L96.45 139.24L90.91 142.47L98.98 144.88C99.65 145.08 100.35 145.08 101.02 144.88L109.09 142.47L103.55 139.24L101.6 131.82Z',
	},
};
